import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowDownToLine, FastForward, TrendingUp, ShieldCheck, Sparkles } from "lucide-react";
import sphere from "@/assets/bio-absorption-sphere.webp";
import sphereSm from "@/assets/bio-absorption-sphere-sm.webp";
import bioAbsorbVideo from "@/assets/bio-absorb-matrix.mp4.asset.json";
import { Reveal } from "@/components/motion/Reveal";
import { AbsorptionRateChart } from "@/components/AbsorptionRateChart";

type Variant = "section" | "badge" | "header";


/**
 * Bio-Absorption Matrix lockup with scroll-linked sphere motion.
 * - section: full editorial band (homepage, about)
 * - header: inline header block (ingredients page)
 * - badge: compact pill (product pages)
 */
export function BioAbsorptionMatrix({
  variant = "section",
  className = "",
}: {
  variant?: Variant;
  className?: string;
}) {
  if (variant === "badge") return <BioAbsorptionBadge className={className} />;
  if (variant === "header") return <BioAbsorptionHeader className={className} />;
  return <BioAbsorptionSection className={className} />;
}

/* ---------- Scroll-linked sphere ---------- */

function AnimatedSphere({ size = "lg" }: { size?: "sm" | "md" | "lg" }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Richer scroll-linked motion: rotation, gentle float, scale lift, halo pulse
  const rotate = useTransform(scrollYProgress, [0, 1], [-25, 25]);
  const y = useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, -40]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.88, 1.04, 0.94]);
  const haloOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.35, 0.8, 0.45]);

  const dims =
    size === "sm"
      ? "h-12 w-12"
      : size === "md"
        ? "h-36 w-36 md:h-56 md:w-56"
        : "h-44 w-44 sm:h-60 sm:w-60 md:h-[22rem] md:w-[22rem] lg:h-[26rem] lg:w-[26rem]";

  return (
    <div ref={ref} className={`relative ${dims} shrink-0`}>
      {/* soft cobalt halo, pulses with scroll */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, color-mix(in oklab, var(--brand) 32%, transparent), transparent 65%)",
          opacity: reduce ? 0.5 : haloOpacity,
        }}
      />
      <motion.img
        src={sphere}
        alt=""
        width={720}
        height={720}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        className="relative h-full w-full object-contain"
        style={reduce ? undefined : { rotate, y, scale, willChange: "transform" }}
        animate={
          reduce
            ? undefined
            : {
                filter: [
                  "drop-shadow(0 20px 40px color-mix(in oklab, var(--brand) 18%, transparent))",
                  "drop-shadow(0 32px 60px color-mix(in oklab, var(--brand) 34%, transparent))",
                  "drop-shadow(0 20px 40px color-mix(in oklab, var(--brand) 18%, transparent))",
                ],
              }
        }
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

/* ---------- Lockup heading (HTML, matches site typography) ---------- */

function MatrixHeading({ compact = false }: { compact?: boolean }) {
  return (
    <div className="text-left">
      <p
        className={`font-display font-bold uppercase tracking-[0.18em] text-brand-deep ${
          compact ? "text-[11px]" : "text-xs md:text-sm"
        }`}
      >
        TruWe&apos;s Proprietary
      </p>
      <h2
        className={`mt-2 font-display font-bold uppercase tracking-tight text-brand ${
          compact
            ? "text-xl leading-[1.05]"
            : "text-3xl leading-[1] md:text-5xl lg:text-6xl"
        }`}
      >
        Bio-Absorb{" "}
        <span className="italic text-muted-foreground">Matrix</span>
      </h2>
      {!compact && (
        <div className="mt-6 space-y-1 font-display text-2xl font-medium leading-[1.2] text-brand-deep md:text-[1.9rem] lg:text-[2.15rem]">
          <p>Formulated for delivery.</p>
          <p>Designed for use.</p>
          <p>Built on research-backed carriers.</p>
        </div>
      )}
    </div>
  );
}

/* ---------- Full editorial section ---------- */

function BioAbsorptionSection({ className = "" }: { className?: string }) {
  const pillars = [
    { icon: ArrowDownToLine, title: "Research-backed\nCarriers", body: "Liposomal and chelated forms chosen for published bioavailability data." },
    { icon: FastForward, title: "Staged\nUptake", body: "Multiple forms and pathways help maintain nutrient availability." },
    { icon: TrendingUp, title: "Cofactor\nSynergy", body: "Minerals and vitamins paired so the body can use them effectively." },
    { icon: ShieldCheck, title: "Protected\nActives", body: "Phospholipid encapsulation helps shield sensitive ingredients." },
    { icon: Sparkles, title: "Clean\nLabel", body: "No synthetic fillers, dyes, or hidden proprietary blends." },
  ];

  return (
    <section
      className={`relative overflow-hidden bg-background py-20 md:py-28 ${className}`}
      aria-labelledby="bio-absorption-matrix"
    >
      <div className="pointer-events-none absolute inset-0 bg-molecules opacity-[0.2]" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        {/* Top: editorial lockup + sphere */}
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16 lg:gap-24">
          <Reveal>
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.32em] text-muted-foreground md:text-xs">
              Science. Synergy. Delivery.
            </p>
            <h2
              id="bio-absorption-matrix"
              className="mt-6 font-display text-[2.75rem] font-light leading-[0.95] tracking-[-0.02em] text-brand-deep md:text-[4.5rem] lg:text-[5.5rem]"
            >
              <span className="block">Bio-Absorb</span>
              <span className="block text-muted-foreground/70">
                Matrix
              </span>
            </h2>
            <div className="mt-6 h-px w-12 bg-border" />
            <p className="mt-8 font-display text-2xl font-light leading-[1.2] text-brand-deep md:text-[1.75rem]">
              Where every ingredient <em className="italic text-brand">supports</em> every other.
            </p>
            <p className="mt-6 max-w-md text-base leading-relaxed text-brand-deep/80">
              Our formulas pair research-backed actives with delivery forms that have published bioavailability data.
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              Each ingredient is chosen and calibrated to work with the rest, liposomal carriers, chelated minerals, and a proprietary phytonutrient blend designed to support absorption and utilisation.
            </p>
            <div className="mt-8 inline-flex items-center rounded-full border border-border/80 px-5 py-2.5 text-sm text-brand-deep">
              Formulated for delivery, not just dose.
            </div>
          </Reveal>
          <Reveal delay={0.08} className="flex justify-center md:justify-end">
            <div className="relative w-full max-w-[26rem]">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-8 rounded-full blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, color-mix(in oklab, var(--brand) 28%, transparent), transparent 65%)",
                }}
              />
              <video
                src={bioAbsorbVideo.url}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="Bio-Absorb Matrix animation"
                className="relative aspect-square w-full rounded-3xl object-contain"
              />
            </div>
          </Reveal>
        </div>

        {/* Middle: live absorption rate chart */}
        <Reveal delay={0.1}>
          <div className="mt-16 md:mt-24">
            <AbsorptionRateChart />
          </div>
        </Reveal>

        {/* Bottom: 5-pillar grid */}
        <Reveal delay={0.12}>
          <div className="mt-16 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-border/70 pt-12 sm:grid-cols-3 md:mt-24 md:grid-cols-5 md:gap-x-8">
            {pillars.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex flex-col items-center text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-border/80 text-brand-deep">
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 whitespace-pre-line font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-deep">
                  {title}
                </h3>
                <p className="mt-3 max-w-[14rem] text-xs leading-relaxed text-muted-foreground">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}


/* ---------- Header variant (Ingredients page) ---------- */

function BioAbsorptionHeader({ className = "" }: { className?: string }) {
  return (
    <div
      className={`relative flex flex-col items-center gap-8 rounded-3xl border border-border/70 bg-card px-6 py-10 md:flex-row md:gap-12 md:px-12 md:py-14 ${className}`}
    >
      <div className="flex w-full justify-center md:w-auto">
        <AnimatedSphere size="md" />
      </div>
      <div className="flex-1">
        <MatrixHeading />
      </div>
    </div>
  );
}

/* ---------- Compact badge (Product page) ---------- */

function BioAbsorptionBadge({ className = "" }: { className?: string }) {
  return (
    <Link
      to="/ingredients"
      hash="bio-absorption-matrix"
      className={`group inline-flex items-center gap-3 rounded-full border border-brand/20 bg-brand-soft/40 px-4 py-2 transition-colors hover:border-brand/40 hover:bg-brand-soft/70 ${className}`}
    >
      <img src={sphereSm} alt="" width={32} height={32} className="h-8 w-8 object-contain" loading="lazy" decoding="async" />
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-deep/70">
          Powered by
        </span>
        <span className="font-display text-sm font-bold uppercase tracking-tight text-brand">
          Bio-Absorption Matrix
        </span>
      </span>
    </Link>
  );
}
